/**
 * Kilometres per trip from the vehicle's odometer, the way a paper logbook records them:
 * KM-Stand Ende minus KM-Stand Start, and nothing else. Geotab stores one odometer reading per
 * trip, taken at the trip end (metres). The start reading of a trip is therefore the end reading
 * of the trip before it, which is why callers pass trips from before the audited period as well.
 * A trip without a reading of its own or of its predecessor has no distance.
 *
 * The readings are never replaced, only flagged for the detail view:
 * - `odometerJump`: the counter went backwards by more than `JUMP_MIN_KM`, or forwards by more
 *   than any vehicle covers between the previous trip's stop and the next trip's start (an
 *   electric bus of a customer alternates between two counters about 60,000 km apart).
 * - `odometerGap`: the difference exceeds what the trip's own duration allows, so it includes
 *   kilometres driven without a trip record before the next recorded trip (a customer vehicle drove
 *   246 km on 29./30.09.2026 without a single trip; Geotab put the reading of the next trip start on the last trip before the gap).
 */
export interface OdometerDistance {
  odometerStartKm?: number;
  odometerEndKm?: number;
  /** Odometer end minus start; undefined when either reading is missing. */
  distance?: number;
  /** True when the readings cannot be right (backwards, or faster than any vehicle drives). */
  odometerJump?: boolean;
  /** True when `distance` includes kilometres driven without a trip record before the next trip. */
  odometerGap?: boolean;
}

const JUMP_MIN_KM = 20;
/** Upper bound for kilometres a vehicle covers per hour. */
const MAX_SPEED_KMH = 130;
const HOUR_MS = 60 * 60 * 1000;

const maxKm = (fromMs: number, toMs: number) => JUMP_MIN_KM + (MAX_SPEED_KMH * Math.max(0, toMs - fromMs)) / HOUR_MS;

interface TripLike {
  id: string;
  start: Date;
  stop?: Date;
  odometer?: number;
}

function endReadingKm(trip: TripLike): number | undefined {
  return typeof trip.odometer === "number" && trip.odometer > 0 ? trip.odometer / 1000 : undefined;
}

/**
 * Distances for `trips` of one vehicle; the order of `trips` does not matter. `until` bounds the
 * time the last trip may have been followed by unrecorded driving (default: now).
 */
export function odometerDistances(trips: TripLike[], until: Date = new Date()): Map<string, OdometerDistance> {
  const sorted = [...trips].sort((a, b) => a.start.getTime() - b.start.getTime());
  const result = new Map<string, OdometerDistance>();
  let previous: { end: number; stopMs: number } | undefined;
  sorted.forEach((trip, index) => {
    const end = endReadingKm(trip);
    if (end == null) {
      result.set(trip.id, {});
      return;
    }
    const stopMs = (trip.stop ?? trip.start).getTime();
    if (!previous) {
      result.set(trip.id, { odometerEndKm: end });
      previous = { end, stopMs };
      return;
    }
    const delta = end - previous.end;
    const nextStartMs = (sorted[index + 1]?.start ?? until).getTime();
    const jump = delta < -JUMP_MIN_KM || delta > maxKm(previous.stopMs, nextStartMs);
    const gap = !jump && delta > maxKm(trip.start.getTime(), stopMs);
    result.set(trip.id, {
      odometerStartKm: previous.end,
      odometerEndKm: end,
      distance: delta,
      ...(jump ? { odometerJump: true } : {}),
      ...(gap ? { odometerGap: true } : {}),
    });
    previous = { end, stopMs };
  });
  return result;
}
